import L from "leaflet";
import "leaflet-draw";
import "leaflet-editable";
import "./lib/measure.js";
import "./lib/leaflet-measure-path.css";
import "leaflet-draw/dist/leaflet.draw.css";
import i18n from "i18next";

// copied from and refactored version of:
// https://github.com/olanaso/Leaflet.MeasurePolygon/blob/main/leaflet.MeasurePolygon.js
// to add support for editing and a measurement panel that shows total area and perimeter

/* eslint-disable camelcase */
L.Control.MeasurePolygon = L.Control.extend({
  statics: {
    TITLE: "Measure area",
  },
  options: {
    position: "topright",
    color_polygon: "black",
    fillColor_polygon: "white",
    weight_polygon: 2,
    width: 150,
    height: 20,
    html_template: `<div class="measure-panel align-items-center">
      <span>_p_area <div class="vertical-divider"></div> _p_perimeter</span>
      <i id="close-icon" class="ml-2 far fa-close"></i>
    </div>`,
    measurementOptions: {
      showOnHover: false,
      showDistances: true,
      showArea: true,
      showTotalDistance: true,
      ha: true,
    },
  },

  onAdd(map) {
    this._map = map;
    this._enabled = false;
    this._isDrawing = false;

    this._tempLabels = L.layerGroup().addTo(map);
    this._measureLayers = L.layerGroup().addTo(map);

    // Control button setup
    const className = "leaflet-control-draw";
    this._container = L.DomUtil.create("div", "leaflet-bar");
    this._button = L.DomUtil.create("a", `${className}-measure`, this._container);
    this._button.href = "#";
    this._button.title = L.Control.MeasurePolygon.TITLE;

    L.DomEvent.on(this._button, "click", L.DomEvent.stopPropagation)
      .on(this._button, "click", L.DomEvent.preventDefault)
      .on(this._button, "click", this.toggle, this);

    this._drawHandler = new L.Draw.Polygon(map, {
      showArea: true,
      shapeOptions: {
        stroke: true,
        color: this.options.color_polygon,
        fillColor: this.options.fillColor_polygon,
        dashArray: "3, 5",
        weight: this.options.weight_polygon,
        fillOpacity: 0.4,
      },
    });

    this._measurePanel = L.control({ position: "topright" });
    this._measurePanel.onAdd = () => {
      const panel = L.DomUtil.create("div", "measure-panel");
      panel.id = "measure-panel";
      panel.style.minWidth = this.options.width + "px";
      panel.style.backgroundColor = "white";
      panel.style.padding = "8px";
      panel.style.borderRadius = "4px";
      this._content = L.DomUtil.create("div", "", panel);
      return panel;
    };

    // --- EVENTS ---
    map.on("draw:drawstart", () => {
      this._isDrawing = true;
      this._tempLabels.clearLayers();
    });

    map.on("draw:drawstop", () => {
      this._isDrawing = false;
      this._tempLabels.clearLayers();
    });

    // Live labels while drawing
    map.on("mousemove", (e) => this._showPolylineLabels(e));

    // Finish polygon
    map.on("draw:created", (event) => {
      const layer = event.layer;

      this._measureLayers.clearLayers();
      this._tempLabels.clearLayers();

      layer
        .addTo(this._measureLayers)
        .showMeasurements(this.options.measurementOptions)
        .enableEdit();

      // restore style
      layer.setStyle({
        stroke: true,
        color: this.options.color_polygon,
        fillColor: this.options.fillColor_polygon,
        dashArray: "3, 5",
        weight: this.options.weight_polygon,
        fillOpacity: 0.4,
      });

      if (!document.getElementById("measure-panel")) {
        this._measurePanel.addTo(map);
      }

      this._updateMeasurements(layer);

      // Update when editing
      map.on("editable:vertex:drag editable:vertex:deleted", () => {
        layer.updateMeasurements();
        this._updateMeasurements(layer);
      });

      this._drawHandler.disable();
      this._isDrawing = false;
    });

    L.DomEvent.on(document, "keydown", (e) => {
      if (e.key === "Escape" && this._enabled) {
        if (this._isDrawing) {
          this._drawHandler.disable();
          this._isDrawing = false;
        } else {
          this._disableMeasure();
        }
      }
    });

    return this._container;
  },

  toggle() {
    if (this._enabled) {
      this._removeActiveMeasure();
    } else {
      this._enableMeasure();
    }
  },

  _enableMeasure() {
    if (this.options.pro) return this.options.pro();
    if (this._enabled) return;
    this._drawHandler.enable();
    this._enabled = true;
    L.DomUtil.addClass(this._container, "enabled");
    if (window.track) window.track("map", "measure_polygon");
  },

  _disableMeasure() {
    if (!this._enabled) return;
    this._drawHandler.disable();
    this._enabled = false;
    this._isDrawing = false;

    L.DomUtil.removeClass(this._container, "enabled");

    this._clearMeasurements();
    this._removePanel();
  },

  _removeActiveMeasure() {
    this._clearMeasurements();
    this._removePanel();
    this._disableMeasure();
  },

  _clearMeasurements() {
    this._measureLayers.clearLayers();
    this._tempLabels.clearLayers();
  },

  _removePanel() {
    const p = document.getElementById("measure-panel");
    if (p) p.remove();
  },

  _showPolylineLabels(e) {
    if (!this._isDrawing || !this._drawHandler._markers) return;

    const markers = this._drawHandler._markers;
    if (markers.length === 0) return;

    const latlngs = markers.map((m) => m.getLatLng());
    latlngs.push(e.latlng);

    // Show tooltip perimeter
    const perimeter = this._calculatePerimeter(latlngs);
    const txt = `${perimeter.toFixed(2)} m`;

    L.drawLocal.draw.handlers.polygon.tooltip.cont = `${txt}<br>${i18n.t(
      "Click on the map to expand the measurement"
    )}`;

    L.drawLocal.draw.handlers.polygon.tooltip.end = `${txt}<br>${i18n.t(
      "Click to expand or click first point to finish"
    )}`;

    // Draw labels
    this._tempLabels.clearLayers();

    for (let i = 0; i < latlngs.length - 1; i++) {
      const a = latlngs[i],
        b = latlngs[i + 1];
      const mid = L.latLng((a.lat + b.lat) / 2, (a.lng + b.lng) / 2);
      const dist = a.distanceTo(b);

      L.marker(mid, {
        interactive: false,
        icon: L.divIcon({
          className: "leaflet-measure-path-measurement",
          html: `<div>${dist.toFixed(2)} m</div>`,
        }),
      }).addTo(this._tempLabels);
    }
  },

  _calculatePerimeter(latlngs) {
    let p = 0;
    for (let i = 0; i < latlngs.length - 1; i++) {
      p += latlngs[i].distanceTo(latlngs[i + 1]);
    }
    return p;
  },

  _updateMeasurements(layer) {
    const latlngs = layer.getLatLngs()[0];
    const area = L.GeometryUtil.geodesicArea(latlngs);

    let circumference = 0;
    for (let i = 0; i < latlngs.length - 1; i++) {
      circumference += latlngs[i].distanceTo(latlngs[i + 1]);
    }
    circumference += latlngs[latlngs.length - 1].distanceTo(latlngs[0]);

    const areaText = area > 10000 ? `${(area / 10000).toFixed(4)} ha` : `${area.toFixed(4)} m²`;

    const circText = `${circumference.toFixed(4)} m`;

    const html = this.options.html_template
      .replace("_p_area", areaText)
      .replace("_p_perimeter", circText);

    if (this._content) {
      this._content.innerHTML = html;

      const closeIcon = document.getElementById("close-icon");
      if (closeIcon) {
        closeIcon.style.cursor = "pointer";
        L.DomEvent.on(closeIcon, "click", this._removeActiveMeasure, this);
      }
    }
  },
});

L.control.measurePolygon = function (options) {
  return new L.Control.MeasurePolygon(options);
};
